<template>
  <div class="container">
    <div class="d-flex">
      <div class="flex-grow-1">
        <h1 class="lyon-display-medium-32">Personal Profile</h1>
      </div>
      <div v-if="canAccessPublicAndAdmin() && !isUserLoading">
        <router-link :to="{ name: 'people-edit'}">
          <i v-b-tooltip.hover class="fa fa-cog edit-icon" title="Quick Edit" />
        </router-link>
      </div>
      <div v-if="canEditCms && !isUserLoading">
        <a :href="fetchLegacyUrl(`/people/${$route.params.id}?isEdit=true`)">
          <i v-b-tooltip.hover class="fa fa-edit edit-icon" title="Full Edit" />
        </a>
      </div>
    </div>
    <info-block-loader v-if="isUserLoading" :property-line="10" :text-line="5" info-type="userProfile" />
    <div v-else>
      <policy-agreement-section :data="peopleData.person" :event-type="'profile'" :event-name="'-'" />
      <div class="row">
        <div class="col-md-4 mb-3" style="max-width : 18rem">
          <div class="profile-image">
            <img :src="fetchImage(peopleData.person.profile_image, 700, 500)" onerror="this.onerror=null;this.src='/img/default-avatar.jpg';">
          </div>
        </div>
        <div class="col-md-8">
          <div
            v-if="peopleData.person.deceased && canEditCms"
            class="px-2 badge regular-14 typo-color-white bg-danger"
          >
            DECEASED
          </div>
          <div class="bold-24 mb-1">{{ peopleData.person.name }}</div>
          <!-- <div class="medium-18 typo-color-light-black mb-2">{{peopleData.person.title_at_institution}}</div> -->
          <div v-for="(homeInstitution, index) in peopleData.person.home_display_person_institution" :key="index" class="mb-2">
            <div
              v-if="checkNullAndEmptyValues(homeInstitution.title)"
              class="medium-18 typo-color-light-black mb-1"
            >
              {{ homeInstitution.title }}
            </div>
            <span class="medium-18">{{ homeInstitution.name }}</span>
            <span v-if="homeInstitution.department" class="medium-14">, {{ homeInstitution.department }}</span>
          </div>
          <div class="row mb-3 mt-4" v-if="checkNullAndEmptyValues(peopleData.person.homepage_url)">
            <div class="col-md-4">
              <span class="text-muted regular-16">Website</span>
            </div>
            <div class="col-md-8">
              <div class="regular-16 custom-html-text">
                <a :href="peopleData.person.homepage_url" target="_blank">
                  {{ peopleData.person.homepage_url }}
                </a>
              </div>
            </div>
          </div>
          <div v-if="canAccessPublicAndAdmin()">
            <div class="row mb-3">
              <div class="col-md-4">
                <span class="text-muted regular-16">Twitter</span>
              </div>
              <div class="col-md-8">
                <div v-if="checkNullAndEmptyValues(peopleData.person.twitter_id)" class="regular-16 custom-html-text">
                  <a :href="peopleData.person.twitter_id" target="_blank">
                    {{ peopleData.person.twitter_id }}
                  </a>
                </div>
                <div v-else>
                  --
                </div>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-md-4">
                <span class="text-muted regular-16">Facebook</span>
              </div>
              <div class="col-md-8">
                <div v-if="checkNullAndEmptyValues(peopleData.person.facebook_id)" class="regular-16 custom-html-text">
                  <a :href="peopleData.person.facebook_id" target="_blank">
                    {{ peopleData.person.facebook_id }}
                  </a>
                </div>
                <div v-else>
                  --
                </div>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-md-4">
                <span class="text-muted regular-16">Linkedin</span>
              </div>
              <div class="col-md-8">
                <div v-if="checkNullAndEmptyValues(peopleData.person.linkedin_id)" class="regular-16 custom-html-text">
                  <a :href="peopleData.person.linkedin_id" target="_blank">
                    {{ peopleData.person.linkedin_id }}
                  </a>
                </div>
                <div v-else>
                  --
                </div>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-md-4">
                <span class="text-muted regular-16">Google Plus</span>
              </div>
              <div class="col-md-8">
                <div v-if="checkNullAndEmptyValues(peopleData.person.google_plus_id)" class="regular-16 custom-html-text">
                  <a :href="peopleData.person.google_plus_id" target="_blank">
                    {{ peopleData.person.google_plus_id }}
                  </a>
                </div>
                <div v-else>
                  --
                </div>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-md-4">
                <span class="text-muted regular-16">
                  PID
                </span>
              </div>
              <div class="col-md-8">
                <div v-if="checkNullAndEmptyValues(peopleData.person.person_pid)" class="regular-16 custom-html-text">
                  {{ peopleData.person.person_pid }}
                </div>
                <div v-else>
                  --
                </div>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-md-4">
                <span class="text-muted regular-16">
                  ORCID
                </span>
              </div>
              <div class="col-md-8">
                <div v-if="peopleData.person.orcid_url" class="regular-16">
                  <a :href="getORCIDUrl(peopleData.person.orcid_id)" target="_blank">
                    {{ getORCIDUrl(peopleData.person.orcid_id) }}
                  </a>
                </div>
                <button v-else class="btn btn-primary btn-sm" @click="signInWithOrcid()">
                  Link your ORCID identifier
                </button>
              </div>
            </div>
          </div>
          <div v-if="canEditCms">
            <div class="row mb-3">
              <div class="col-md-4">
                <span class="text-muted regular-16">
                  Socially Addressed As
                </span>
              </div>
              <div class="col-md-8">
                <div v-if="checkNullAndEmptyValues(peopleData.person.social_name)" class="regular-16 custom-html-text">
                  {{ peopleData.person.social_name }}
                </div>
                <div v-else>
                  --
                </div>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-md-4">
                <span class="text-muted regular-16">
                  Informal Name
                </span>
              </div>
              <div class="col-md-8">
                <div v-if="checkNullAndEmptyValues(peopleData.person.informal_name)" class="regular-16 custom-html-text">
                  {{ peopleData.person.informal_name }}
                </div>
                <div v-else>
                  --
                </div>
              </div>
            </div>
          </div>
          <div v-if="checkNullAndEmptyValues(peopleData.person.visit_dates) || canAccessPublicAndAdmin()" class="row mb-3">
            <div class="col-md-4">
              <span class="text-muted regular-16">Visit Date</span>
            </div>
            <div class="col-md-8 regular-16 custom-html-text">
              <span v-if="checkNullAndEmptyValues(peopleData.person.visit_dates)">
                {{ peopleData.person.visit_dates }}
              </span>
              <span v-else>
                --
              </span>
            </div>
          </div>
          <div v-if="checkNullAndEmptyValues(peopleData.person.program_name) || canAccessPublicAndAdmin()" class="row mb-3">
            <div class="col-md-4">
              <span class="text-muted regular-16">Program</span>
            </div>
            <div class="col-md-8">
              <span v-if="checkNullAndEmptyValues(peopleData.person.program_name)">
                <router-link :to="{
                  name: 'programs-details',
                  params: { id: peopleData.person.program_id }}"
                >
                  <span class="regular-16 typo-color-blue custom-html-text">{{ peopleData.person.program_name }}</span>
                </router-link>
              </span>
              <span v-else>
                --
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import mixin from "@/utils/mixin.js"
import InfoBlockLoader from "@/components/Loaders/InfoBlockLoader.vue"
import PolicyAgreementSection from "@/components/AboutUs/Directories/PolicyAgreementSection.vue"

export default {
	name: "PersonalDetailSection",
	mixins: [mixin],
	components: {
		InfoBlockLoader,
		PolicyAgreementSection,
	},
	props: ["peopleData", "isUserLoading"],
	methods: {
		getORCIDUrl(orcidId) {
			return `https://orcid.org/${orcidId}`
		},
	},
}
</script>

<style scoped lang="scss">
div.profile-image{
  height: 275px;
  display: table-cell;
  text-align: center;
  vertical-align: middle;
  width: 212px;
  img{
    object-fit: cover;
    width: 100%;
    display: block !important;
    height: 290px;
  }
}
</style>
